import React, { useState, useEffect, useCallback } from 'react';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { load as loadTeam } from 'js/actions/team-actions';
import { loadSubscription } from 'js/actions/assessment-template-actions';

import { FullPageLoading } from 'js/components';
import {
  createDraft,
  deleteDraft,
  getTemplateState,
  listDrafts,
} from 'js/api/assessment-drafts';
import { useNotify } from 'js/hooks/useNotify';
import {
  draftSourceKey,
  draftType,
  forkFromQuestions,
  forkFromTemplateState,
  isOwnedTemplate,
} from 'js/utils/assessment-draft';

import { getEngineStrategies, getEngineForTemplate } from 'js/engines';
import {
  assessmentPayloadFromTemplate,
  shouldSkipConfigure,
} from 'js/utils/workspace-template';

import templates from 'js/data/assessment-templates';

function findSubscription(subscriptions, assessmentKey) {
  if (!assessmentKey) {
    return undefined;
  }

  return (
    subscriptions[assessmentKey] ??
    Object.values(subscriptions).find((s) => s.key === assessmentKey)
  );
}

const ConfigureAssessmentContainer = ({
  match: {
    params: { assessmentKey, teamId },
  },
  history,
  team,
  subscription,
  workspaceId,
  createAssessment,
  loadTeam,
  loadSubscription,
}) => {
  const [template, setTemplate] = useState(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(null);
  const token = useSelector((state) => state.identity.token);
  const notify = useNotify();

  const handleCreateAssessment = useCallback(
    (assessment, replaceHistory = false) => {
      setCreating(true);

      return createAssessment({
        ...assessment,
        team_id: teamId,
      }).then((assessment) => {
        setCreating(false);
        const strategies = getEngineStrategies(assessment);
        const navigate =
          replaceHistory === true ? history.replace : history.push;

        navigate(strategies.buildPostCreateRedirectPath(assessment));
      });
    },
    [teamId, createAssessment, history.replace, history.push]
  );

  useEffect(() => {
    loadTeam(teamId);
  }, [teamId, loadTeam]);

  useEffect(() => {
    loadSubscription(assessmentKey);
  }, [assessmentKey, loadSubscription]);

  useEffect(() => {
    let template =
      templates.find((template) => template.key === assessmentKey) ??
      subscription;

    if (shouldSkipConfigure(template)) {
      handleCreateAssessment(assessmentPayloadFromTemplate(template), true);
    } else {
      setTemplate(template);
    }
  }, [assessmentKey, teamId, handleCreateAssessment, subscription]);

  const sourceKey = template ? draftSourceKey(template) : null;

  // A draft this person started customizing earlier, to pick back up
  useEffect(() => {
    if (!sourceKey) {
      return;
    }

    listDrafts(token, workspaceId, { teamId, sourceKey })
      .then(([latest]) => setDraft(latest ?? null))
      .catch(() => setDraft(null));
  }, [token, workspaceId, teamId, sourceKey]);

  const openDraft = useCallback(
    (draftId) =>
      history.push(`/w/${workspaceId}/assessments/drafts/${draftId}/edit`),
    [history, workspaceId]
  );

  // A template the workspace owns is forked live so its edits can be merged
  // back; anything else is rebuilt from the questions selected here. An owned
  // template whose live document can't be read fails outright rather than
  // quietly becoming a draft that can't update it.
  const handleCustomize = useCallback(
    async ({ questionSets, excludedKeys }) => {
      try {
        let fork;

        if (isOwnedTemplate(template)) {
          const state = await getTemplateState(
            token,
            workspaceId,
            template.template_id
          );

          fork = {
            // Customize what teams get: the last published version
            ...forkFromTemplateState(state.update, excludedKeys, {
              published: template.assessment,
              config: template.config,
            }),
            sourceTemplateId: template.template_id,
          };
        } else {
          fork = forkFromQuestions(template, questionSets);
        }

        const created = await createDraft(token, workspaceId, {
          team_id: teamId,
          source_template_id: fork.sourceTemplateId ?? null,
          source_baseline: fork.baseline,
          source_key: draftSourceKey(template),
          type: draftType(template),
          name: template.name,
          initial_update: fork.initialUpdate,
        });

        openDraft(created.id);
      } catch (error) {
        console.error('Failed to start customizing', error);
        notify({
          type: 'danger',
          message: 'There was a problem opening the editor.',
        });
      }
    },
    [template, token, workspaceId, teamId, openDraft, notify]
  );

  const handleDiscardDraft = useCallback(
    () => deleteDraft(token, workspaceId, draft.id).then(() => setDraft(null)),
    [token, workspaceId, draft]
  );

  const { Configure: ConfigurationComponent } = getEngineForTemplate(template);

  return template && team ? (
    <ConfigurationComponent
      template={template}
      team={team}
      workspaceId={workspaceId}
      onCreateAssessment={handleCreateAssessment}
      creating={creating}
      draft={draft}
      onCustomize={handleCustomize}
      onResumeDraft={() => openDraft(draft.id)}
      onDiscardDraft={handleDiscardDraft}
    />
  ) : (
    <FullPageLoading />
  );
};

export default withRouter(
  connect(
    ({ currentWorkspaceId, entities }, { match }) => {
      const { teams = {}, assessmentDocTemplateSubscriptions = {} } = entities;
      const {
        params: { teamId, assessmentKey },
      } = match;

      return {
        team: teams[teamId],
        workspaceId: currentWorkspaceId,
        subscription: findSubscription(
          assessmentDocTemplateSubscriptions,
          assessmentKey
        ),
      };
    },
    {
      loadTeam,
      loadSubscription,
      createAssessment: (assessment) => {
        const { create } = getEngineStrategies(assessment);

        return create(assessment);
      },
    }
  )(ConfigureAssessmentContainer)
);
